import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { BUILD_INFO } from '../../BUILD_INFO.js';
import { ChangePasswordModal } from '../auth/change-password-modal.jsx';
import { SystemClock } from './system-clock.jsx';
import { BellWidget } from '../notifications/bell-widget.jsx';
import { usePWA } from '../../lib/pwa.jsx';

const MAIN_GROUP = [
  { to: '/',                icon: 'fa-gauge-high',          label: 'Dashboard' },
  { to: '/map',             icon: 'fa-map-location-dot',    label: 'Map' },
  { to: '/nodes',           icon: 'fa-network-wired',       label: 'Nodes' },
];

const CATALOG_GROUP = [
  { to: '/oss',             icon: 'fa-sitemap',             label: 'OSS' },
  { to: '/channels',        icon: 'fa-comments',            label: 'Channels' },
  { to: '/personnel',       icon: 'fa-users',               label: 'Personnel' },
];

// COP ADMS integration — operator-driven views into the local
// ad-cop-service. Proxied through /api/cop-proxy on this coord
// (X-API-Key auth — see server/src/routes/cop-proxy.js).
const COP_GROUP = [
  { to: '/cop-datasets',     icon: 'fa-layer-group',     label: 'Datasets' },
  { to: '/cop-cot-bridge',   icon: 'fa-satellite-dish',  label: 'CoT Bridge' },
  { to: '/cop-nffi-bridge',  icon: 'fa-handshake-angle', label: 'NFFI Bridge' },
  { to: '/cop-adatp3-bridge',icon: 'fa-file-lines',      label: 'ADatP-3 Bridge' },
  { to: '/cop-orders',       icon: 'fa-clipboard-list',  label: 'ADatP-3 Orders' },
  { to: '/cop-locations',    icon: 'fa-location-crosshairs', label: 'Locations' },
  { to: '/cop-intelligence', icon: 'fa-binoculars',      label: 'Intelligence' },
];

// Phase 2.5 step 1 — PoC observability sidebar group. Live sessions
// surface lands first; recordings browser arrives with Phase 3.7.
const POC_GROUP = [
  { to: '/poc-sessions',     icon: 'fa-walkie-talkie',   label: 'PoC Sessions' },
  { to: '/poc-recordings',   icon: 'fa-circle-dot',      label: 'PoC Recordings' },
];

const ADMIN_GROUP = [
  { to: '/settings',        icon: 'fa-sliders',             label: 'Settings' },
  { to: '/users',           icon: 'fa-user-gear',           label: 'Users' },
  { to: '/certificates',    icon: 'fa-shield',              label: 'Certificates' },
  { to: '/policy',          icon: 'fa-broadcast-tower',     label: 'Policy' },
  { to: '/notifications',   icon: 'fa-bell',                label: 'Notifications' },
  { to: '/logs',            icon: 'fa-clipboard-list',      label: 'Logs' },
  { to: '/audit',           icon: 'fa-shield-halved',       label: 'Audit' },
  // Etap 2 Milestone E (2026-07-11) — diagnostic RPC to a home terminal.
  // Admin-role-only (page enforces the gate itself).
  { to: '/rpc-console',     icon: 'fa-terminal',            label: 'RPC Console' },
];

function Item({ to, icon, label }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        [
          'group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-colors',
          isActive
            ? 'bg-accent-soft text-accent'
            : 'text-ink/80 hover:bg-canvas hover:text-ink',
        ].join(' ')
      }
    >
      <i
        className={`fa-solid ${icon} w-4 text-[15px] text-mist group-hover:text-ink`}
        aria-hidden="true"
      />
      <span>{label}</span>
    </NavLink>
  );
}

/**
 * Section divider with a small label. Provides the visual rhythm between
 * navigation groups (Main / Catalog / Administration / PWA / Sign-Out).
 */
function SectionDivider({ label }) {
  return (
    <div className="mt-5 mb-2 flex items-center gap-2 px-3">
      <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-mist/80">
        {label}
      </span>
      <div className="flex-1 border-t border-line" />
    </div>
  );
}

/**
 * Live PWA action button — driven by the install/update state machine in
 * lib/pwa.jsx. Shape matches an active <Item> exactly (same padding /
 * radius / typography); only the colour palette differs (amber instead
 * of the active-accent blue) so the three sidebar "pills" — active menu,
 * PWA action, system clock — read as one family.
 *
 *   updateReady → "Update PWA" (amber, animated dot, activates waiting SW)
 *   canInstall  → "Install PWA" (amber, triggers deferred beforeinstallprompt)
 *   neither     → component returns null (hidden)
 */
function PwaItem() {
  const { canInstall, updateReady, install, update, clientBuild, serverBuild } = usePWA();
  if (!updateReady && !canInstall) return null;

  const onClick = updateReady ? update : install;
  // Tooltip exposes the exact client/server build numbers so a stuck
  // "Update PWA" can be diagnosed at a glance without DevTools.
  const tooltip = updateReady
    ? `A new version is available\nclient: v${clientBuild || 'dev'}\nserver: v${serverBuild || '?'}`
    : 'Install as an app';
  return (
    <button
      type="button"
      onClick={onClick}
      title={tooltip}
      className="flex w-full items-center gap-3 rounded-2xl bg-warning-soft px-3 py-2.5 text-sm font-medium text-warning transition-colors hover:bg-warning/15"
    >
      <i
        className={`fa-solid ${updateReady ? 'fa-arrows-rotate' : 'fa-cloud-arrow-down'} w-4 text-[15px]`}
        aria-hidden="true"
      />
      <span className="flex-1 text-left">{updateReady ? 'Update PWA' : 'Install PWA'}</span>
      {updateReady && (
        <span className="h-2 w-2 animate-pulse rounded-full bg-warning" aria-hidden="true" />
      )}
    </button>
  );
}

export default function Sidebar({ user, onSignOut }) {
  const [pwOpen, setPwOpen] = useState(false);
  const { clientBuild, serverBuild, updateReady } = usePWA();
  const year = new Date().getUTCFullYear();

  return (
    <>
      <aside className="flex h-full w-72 shrink-0 flex-col border-r border-line bg-white">
        {/* Header — bigger logo, room is available */}
        <div className="flex items-center gap-3 border-b border-line px-5 py-5">
          <img
            src="/runtime-assets/vendor/logos/logo.svg"
            alt="TMS"
            className="h-14 w-14 rounded-xl border border-line bg-canvas p-1.5"
          />
          <div className="leading-tight">
            <div className="text-base font-semibold tracking-tight text-ink">TMS ADMS</div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-mist">Coordinator</div>
          </div>
        </div>

        {/* Current user pill */}
        <div className="border-b border-line px-4 py-3">
          <div className="flex items-center gap-3 rounded-2xl border border-line bg-canvas px-3 py-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-soft text-sm font-semibold text-accent">
              {(user?.displayName || user?.login || '?').slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-[13px] font-semibold text-ink">
                {user?.displayName || user?.login || 'Unknown'}
              </div>
              <button
                type="button"
                onClick={() => setPwOpen(true)}
                className="mt-0.5 inline-flex items-center gap-1.5 text-[11px] font-medium text-mist transition-colors hover:text-accent"
              >
                <i className="fa-solid fa-key text-[10px]" aria-hidden="true" />
                Change password
              </button>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <BellWidget viewerTimezone={user?.timezone || 'UTC'} />
              {user?.role === 'admin' && (
                <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
                  Admin
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Menu */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-thin">
          <div className="space-y-1">
            {MAIN_GROUP.map((it) => (
              <Item key={it.to} {...it} />
            ))}
          </div>

          <SectionDivider label="Catalog" />

          <div className="space-y-1">
            {CATALOG_GROUP.map((it) => (
              <Item key={it.to} {...it} />
            ))}
          </div>

          <SectionDivider label="COP ADMS" />

          <div className="space-y-1">
            {COP_GROUP.map((it) => (
              <Item key={it.to} {...it} />
            ))}
          </div>

          <SectionDivider label="PoC" />

          <div className="space-y-1">
            {POC_GROUP.map((it) => (
              <Item key={it.to} {...it} />
            ))}
          </div>

          <SectionDivider label="Administration" />

          <div className="space-y-1">
            {ADMIN_GROUP.map((it) => (
              <Item key={it.to} {...it} />
            ))}
          </div>

          <div className="my-3" />
          <PwaItem />

          <div className="my-4 border-t border-line" />

          <button
            type="button"
            onClick={onSignOut}
            className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium text-ink/80 transition-colors hover:bg-rose-soft hover:text-rose-700"
          >
            <i className="fa-solid fa-arrow-right-from-bracket w-4 text-[15px]" aria-hidden="true" />
            <span>Sign Out</span>
          </button>
        </nav>

        {/* System clock */}
        <div className="px-4 pb-3">
          <SystemClock timezone={user?.timezone} />
        </div>

        {/* Footer — copyright on top, build on its own row.
            Two short lines fit the 288px sidebar even at long version strings;
            on mismatch the row swaps to "old → new" with amber accent and a
            tooltip carrying the full pair for diagnosis. */}
        <div className="border-t border-line px-5 py-2.5 text-[11px] leading-tight text-mist">
          <div className="flex items-center gap-1.5">
            <i className="fa-regular fa-copyright text-mist/70" aria-hidden="true" />
            <span>{year} MicroGIS&trade;</span>
          </div>
          <div
            className={`mt-0.5 flex items-center gap-1.5 font-mono ${updateReady ? 'text-warning' : ''}`}
            title={`client: v${clientBuild || 'dev'}\nserver: v${serverBuild || '?'}`}
          >
            <i className="fa-solid fa-code-branch text-mist/70" aria-hidden="true" />
            {/* Both clientBuild and serverBuild are normalised (no v-prefix)
                in pwa.jsx — comparison and display use the same shape. */}
            {serverBuild && serverBuild !== clientBuild ? (
              <span className="truncate">
                v{BUILD_INFO.version}
                <span aria-hidden="true" className="mx-1">→</span>
                v{serverBuild}
              </span>
            ) : (
              <span className="truncate">v{BUILD_INFO.version}</span>
            )}
          </div>
        </div>
      </aside>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </>
  );
}
